export type Money = { amount: string; currency: 'CAD' }
export type AttentionItem = { id: string; kind: 'overdue' | 'review' | 'deadline' | 'failure'; title: string; detail: string; due: string; href: string }
export type Dashboard = {
  asOf: string
  company: string
  cash: Money
  receivables: Money
  payables: Money
  attention: AttentionItem[]
  deadlines: { label: string; due: string; status: 'ready' | 'draft' | 'blocked' }[]
  activity: { id: string; type: string; description: string; amount: Money; state: string }[]
}
export type Session = { id: string; name: string; locale: string; role: 'owner' | 'accountant' | 'payroll_manager' | 'system_administrator' }
export type ContactRecord = { id: string; code: string; name: string; taxIdentifier: string | null }
export type InvoiceRecord = { id: string; number: string; party: string; date: string | null; total: Money; amountDue: Money; state: string; version: number }
export type CheckoutResult = { checkoutId: string; paymentUrl: string; state: string }
export type Collection<T> = { items: T[]; count: number; nextCursor?: string | null }
export type StableRecord = { id: string; resourceType: string; version: number; [key: string]: unknown }
export type TaxSettings = {
  registrations: { tax: 'gst' | 'qst'; label: string; status: 'not_registered' | 'registered' | 'cancelled'; effectiveFrom: string | null; filingFrequency: 'monthly' | 'quarterly' | 'annual' | null; confirmedOn: string }[]
  collectionEnabled: boolean
  registrationThreshold: Money & { window: string }
  referenceRates: { code: string; rate: string; effectiveFrom: string; sourceUrl: string }[]
}

const API_ORIGIN = import.meta.env?.VITE_API_ORIGIN ?? 'https://api.fortus.space'
const DATABASE = import.meta.env?.VITE_TRYTON_DATABASE ?? 'fortus'
const base = `${API_ORIGIN}/${DATABASE}/fortus/v1`

async function responseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { errors?: { message: string }[] } | null
    throw new Error(payload?.errors?.[0]?.message ?? `Request failed (${response.status})`)
  }
  return response.json() as Promise<T>
}

export async function getSession(): Promise<Session> {
  return responseJson(await fetch(`${base}/session`, { credentials: 'include', headers: { Accept: 'application/json' } }))
}

export async function getDashboard(): Promise<Dashboard> {
  return responseJson(await fetch(`${base}/dashboard`, { credentials: 'include', headers: { Accept: 'application/json' } }))
}

async function getCollection<T>(path: string): Promise<Collection<T>> {
  return responseJson(await fetch(`${base}/${path}?limit=100`, { credentials: 'include', headers: { Accept: 'application/json' } }))
}

export const getContacts = () => getCollection<ContactRecord>('contacts')
export const getInvoices = () => getCollection<InvoiceRecord>('invoices')
export const getSupplierBills = () => getCollection<InvoiceRecord>('supplier-bills')

export async function getRecords(resource: string, state?: string): Promise<Collection<StableRecord>> {
  const query = new URLSearchParams({ limit: '100' })
  if (state) query.set('state', state)
  return responseJson(await fetch(`${base}/records/${encodeURIComponent(resource)}?${query}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }))
}

export async function getRecord(id: string): Promise<StableRecord> {
  return responseJson(await fetch(`${base}/records/item/${encodeURIComponent(id)}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }))
}

export async function getTaxSettings(): Promise<TaxSettings> {
  return responseJson(await fetch(`${base}/tax-settings`, { credentials: 'include', headers: { Accept: 'application/json' } }))
}

export async function createInvoiceCheckout(invoice: InvoiceRecord): Promise<CheckoutResult> {
  return responseJson(await fetch(`${base}/invoices/${invoice.id}/checkout`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Idempotency-Key': `invoice-${invoice.id}-revision-${invoice.version}`,
      'If-Match': `"${invoice.version}"`,
    },
  }))
}

export type ActionRequest = {
  action: string
  input: Record<string, unknown>
  targetId?: string
  expectedVersions?: Record<string, number>
}

async function postAction<T>(path: string, payload: object): Promise<T> {
  return responseJson(await fetch(`${base}/actions/${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }))
}

export async function performAction(request: ActionRequest): Promise<Record<string, unknown>> {
  const idempotencyKey = `web-${request.action}-${crypto.randomUUID()}`
  const prepared = await postAction<{ planToken: string }>('prepare', {
    ...request,
    expectedVersions: request.expectedVersions ?? {},
    idempotencyKey,
  })
  return postAction('commit', { planToken: prepared.planToken, idempotencyKey })
}

export const emptyDashboard: Dashboard = {
  asOf: new Date(0).toISOString(),
  company: 'Solutions TI Fortus inc.',
  cash: { amount: '0.00', currency: 'CAD' },
  receivables: { amount: '0.00', currency: 'CAD' },
  payables: { amount: '0.00', currency: 'CAD' },
  attention: [],
  deadlines: [],
  activity: [],
}
